import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Home, Calendar, CreditCard, LogOut, BookOpen, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { schedulesAPI, bookingsAPI } from '../services/api';
import { format, parseISO, isToday, isFuture, startOfDay, endOfDay } from 'date-fns';

const navItems = [
  { to: '/customer/dashboard', icon: Home, label: 'Dashboard' },
  { to: '/customer/schedule', icon: Calendar, label: 'Studio Schedule' },
  { to: '/customer/classes', icon: BookOpen, label: 'Your Classes' },
  { to: '/customer/community', icon: Users, label: 'Community' },
  { to: '/customer/subscription', icon: CreditCard, label: 'Subscription' },
];

// Helper function to convert UUID to short ID (same as business Sidebar)
const getShortId = (id) => {
  if (!id) return null;
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash) + id.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString().slice(-6).padStart(4, '0');
};

export default function CustomerSidebar() {
  const { studio, user, logout } = useAuth();

  // Get studio ID - use actual ID from database
  const studioId = studio?.id || studio?.studioId;
  const studioName = studio?.name || 'Your Studio';
  const studioShortId = studioId ? getShortId(studioId) : null;

  // Fetch schedules for today to show count badge
  const today = new Date();
  const todayStart = startOfDay(today);
  const todayEnd = endOfDay(today);
  const todayDayOfWeek = today.getDay();

  const { data: todaySchedulesData } = useQuery({
    queryKey: ['schedules-today', studioId, format(today, 'yyyy-MM-dd')],
    queryFn: () => {
      // Fetch schedules for today's date range
      return schedulesAPI.getSchedules(studioId, {
        startDate: format(todayStart, 'yyyy-MM-dd'),
        endDate: format(todayEnd, 'yyyy-MM-dd')
      });
    },
    enabled: !!studioId,
    refetchInterval: false,
  });

  // Count schedules for today
  const todayScheduleCount = (() => {
    if (!todaySchedulesData?.data?.schedules) return 0;

    const schedules = todaySchedulesData.data.schedules;
    let count = 0;

    schedules.forEach(schedule => {
      // Check one-time schedules
      if (schedule.specificDate) {
        const scheduleDate = parseISO(schedule.specificDate);
        if (isToday(scheduleDate)) {
          count++;
        }
      }
      // Check weekly schedules
      else if (schedule.dayOfWeek !== null && schedule.dayOfWeek !== undefined) {
        if (schedule.dayOfWeek === todayDayOfWeek) {
          count++;
        }
      }
    });

    return count;
  })();

  // Fetch user's bookings to count upcoming classes
  const { data: myBookingsData } = useQuery({
    queryKey: ['my-bookings', user?.id],
    queryFn: () => bookingsAPI.getMyBookings(user?.id),
    enabled: !!user?.id,
    retry: false,
    refetchInterval: false,
  });

  // Count upcoming classes (future or today)
  const upcomingClassesCount = (() => {
    if (!myBookingsData?.data) return 0;
    
    const bookings = myBookingsData.data;
    let count = 0;

    bookings.forEach(booking => {
      // Only count bookings with specific dates (one-time classes)
      if (booking.schedule?.specificDate) {
        const classDate = parseISO(booking.schedule.specificDate);
        // Count if the class is today or in the future
        if (isToday(classDate) || isFuture(classDate)) {
          count++;
        }
      }
    });

    return count;
  })();

  return (
    <aside className="w-64 flex flex-col" style={{ background: 'var(--bg-surface)', borderRight: '1px solid var(--border-subtle)' }}>
      {/* Logo/Brand */}
      <div className="p-6 border-b border-[var(--border-subtle)]">
        <h1 className="text-2xl font-bold text-[var(--text-secondary)]">My Studio Class</h1>
        <p className="text-sm text-[var(--text-secondary)] mt-1 truncate">{studioName}</p>
        {studioShortId && (
          <div className="mt-2 p-2 bg-primary-50 rounded">
            <p className="text-xs font-semibold text-[var(--text-primary)] font-mono">
              ID: {studioShortId}
            </p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const showScheduleBadge = item.to === '/customer/schedule' && todayScheduleCount > 0;
          const showClassesBadge = item.to === '/customer/classes' && upcomingClassesCount > 0;
          const badgeCount = showScheduleBadge ? todayScheduleCount : (showClassesBadge ? upcomingClassesCount : 0);
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary-100 text-[var(--text-primary)] font-medium'
                    : 'text-[var(--text-secondary)] hover:bg-secondary-100'
                }`
              }
            >
              <Icon className="w-5 h-5" />
              <span className="flex-1">{item.label}</span>
              {(showScheduleBadge || showClassesBadge) && (
                <span className="bg-primary-600 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">
                  {badgeCount}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-[var(--border-subtle)]">
        <button
          onClick={logout}
          className="flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 w-full transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
}

